import DeleteDialog from 'core/components/shared/DeleteDialog';
import { Utils } from 'core/utils';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ListToolbar from 'cms/components/ListToolbar';
import { UseJobList } from '../hooks';
import { JobHttpService } from '../service';
import AdminJobEditDialog from './dialogs/AdminJobEditDialog';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Tabs,
    Tab
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';

const initialFilters = {
    title: '',
    company: '',
    category: '',
    country: '',
    city: ''
};

const formatText = (value, fallback = '—') => {
    const normalized = Utils.normalizeText ? Utils.normalizeText(value, fallback) : value;
    if (typeof normalized === 'string') {
        return normalized || fallback;
    }
    return normalized ?? fallback;
};

function JobList() {
    const [filters, setFilters] = useState(initialFilters);
    const [appliedFilters, setAppliedFilters] = useState({});
    const [deleteDialogIsOpen, setDeleteDialogIsOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [activeTab, setActiveTab] = useState(0); // 0 = active, 1 = archived
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

    const { data, isLoading, isFetching, removeItemFromList, refetch } = UseJobList({
        size: 50,
        filters: {
            ...appliedFilters,
            archived: activeTab === 1 ? '1' : '0' // '1' for archived, '0' for active
        }
    });

    const jobs = data?.results ?? [];

    const activeFilterEntries = useMemo(
        () => Object.entries(appliedFilters).filter(([, value]) => Boolean(value)),
        [appliedFilters]
    );

    const cleanFilters = (filterObject) =>
        Object.entries(filterObject).reduce((acc, [key, value]) => {
            if (value !== '' && value !== null && value !== undefined) {
                acc[key] = value;
            }
            return acc;
        }, {});

    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleApplyFilters = () => {
        setAppliedFilters(cleanFilters(filters));
    };

    const handleResetFilters = () => {
        setFilters(initialFilters);
        setAppliedFilters({});
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        // Reset filters when switching tabs
        setAppliedFilters({});
        setFilters(initialFilters);
    };

    const handleDeleteRequest = (jobId) => {
        setSelectedId(jobId);
        setDeleteDialogIsOpen(true);
    };

    const handleDeleteConfirmed = () => {
        setDeleteDialogIsOpen(false);
        if (selectedId) {
            removeItemFromList(selectedId);
        }
    };

    const handleArchiveToggle = async (job, shouldArchive) => {
        try {
            let payload;

            if (shouldArchive) {
                const today = new Date();
                const yesterday = new Date(today);
                yesterday.setDate(today.getDate() - 1);
                const isoYesterday = yesterday.toISOString().slice(0, 10); // YYYY-MM-DD

                // Mark as expired in the past so it appears in the archived tab
                payload = { end_date: isoYesterday, is_completed: true };
            } else {
                // Unarchive: clear expiry and completion flag
                payload = { end_date: null, is_completed: false };
            }

            await JobHttpService.partialUpdateJob(job.id, payload);
            await refetch();
        } catch (error) {
            console.error('Failed to update archived state', error);
        }
    };

    const renderStatusChip = (isActive) => {
        if (isActive) {
            return (
                <Chip
                    icon={<CheckCircleOutlineIcon fontSize="small" />}
                    label="Active"
                    color="success"
                    variant="outlined"
                    size="small"
                />
            );
        }

        return (
            <Chip
                icon={<HighlightOffIcon fontSize="small" />}
                label="Inactive"
                color="default"
                variant="outlined"
                size="small"
            />
        );
    };

    const handleCreateJob = async (payload) => {
        // When creating a new job via this dialog, the backend expects a boolean status.
        // Let the API apply its default by omitting the status field on create.
        const { status, ...createPayload } = payload || {};
        await JobHttpService.createJob(createPayload);
        await refetch();
    };

    return (
        <Stack spacing={2}>
            <ListToolbar
                title="Jobs"
                subtitle={data?.count != null ? `${data.count} ${activeTab === 1 ? 'archived' : 'active'} job${data.count === 1 ? '' : 's'}` : undefined}
                actions={
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsCreateDialogOpen(true)}>
                        Add New Job
                    </Button>
                }
                search={{
                    value: filters.title,
                    onChange: (value) => setFilters((prev) => ({ ...prev, title: value })),
                    onSubmit: handleApplyFilters,
                    onClear: () => setAppliedFilters((prev) => { const next = { ...prev }; delete next.title; return next; }),
                    placeholder: 'Search jobs by title…'
                }}
                filterFields={
                    <Grid container spacing={1.5}>
                        {[
                            ['company', 'Company'],
                            ['category', 'Category'],
                            ['country', 'Country'],
                            ['city', 'City']
                        ].map(([name, label]) => (
                            <Grid item xs={12} sm={6} md={3} key={name}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={label}
                                    name={name}
                                    value={filters[name]}
                                    onChange={handleFilterChange}
                                    onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                                />
                            </Grid>
                        ))}
                    </Grid>
                }
                activeFilters={activeFilterEntries}
                onRemoveFilter={(key) => {
                    setAppliedFilters((prev) => { const next = { ...prev }; delete next[key]; return next; });
                    setFilters((prev) => ({ ...prev, [key]: '' }));
                }}
                onApplyFilters={handleApplyFilters}
                onResetFilters={handleResetFilters}
                onRefresh={() => refetch()}
                refreshing={isFetching}
            >
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}
                >
                    <Tab label="Active" />
                    <Tab label="Archived" />
                </Tabs>
            </ListToolbar>

            <Box>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell>Salary</TableCell>
                                <TableCell>Recruiter</TableCell>
                                <TableCell>Company</TableCell>
                                <TableCell>End Date</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center">
                                        <CircularProgress size={28} />
                                    </TableCell>
                                </TableRow>
                            ) : jobs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center">
                                        <Typography variant="body2" color="text.secondary">
                                            {activeTab === 1 
                                                ? 'No archived jobs found with the current filters.'
                                                : 'No active jobs found with the current filters.'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                jobs.map((job) => (
                                    <TableRow key={job.id}>
                                        <TableCell>{job.id}</TableCell>
                                        <TableCell>
                                            <Stack spacing={0.5}>
                                                <Typography
                                                    component={Link}
                                                    to={`/cms/job/${job.id}`}
                                                    fontWeight={600}
                                                    sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                                >
                                                    {formatText(job.title)}
                                                </Typography>
                                               
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            {formatText(job.salary) || `${job.pay_minimum ?? '-'} - ${job.pay_maximum ?? '-'}`}
                                        </TableCell>
                                        <TableCell>
                                            {job.user
                                                ? `${job.user.profile?.first_name ?? ''} ${job.user.profile?.last_name ?? ''}`
                                                : '—'}
                                        </TableCell>
                                        <TableCell>{formatText(job.company?.title ?? job.companyy?.title)}</TableCell>
                                        <TableCell>
                                            {job.end_date ? (
                                                <Stack spacing={0.5}>
                                                    <Typography variant="body2">
                                                        {Utils.getDate(job.end_date)}
                                                    </Typography>
                                                    {new Date(job.end_date) < new Date() && (
                                                        <Chip 
                                                            label="Expired" 
                                                            size="small" 
                                                            color="error" 
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Stack>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    No end date
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{renderStatusChip(activeTab === 0)}</TableCell>
                                        <TableCell>{job.created_at ? Utils.getDate(job.created_at) : '—'}</TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <Tooltip title="Open details">
                                                    <IconButton color="primary"
                                                        size="small"
                                                        component={Link}
                                                        to={`/cms/job/${job.id}`}
                                                    >
                                                        <EditOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete job">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteRequest(job.id)}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                {activeTab === 0 ? (
                                                    <Tooltip title="Archive job">
                                                        <IconButton
                                                            size="small"
                                                            color="warning"
                                                            onClick={() => handleArchiveToggle(job, true)}
                                                        >
                                                            <ArchiveIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title="Unarchive job">
                                                        <IconButton
                                                            size="small"
                                                            color="warning"
                                                            onClick={() => handleArchiveToggle(job, false)}
                                                        >
                                                            <UnarchiveIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <DeleteDialog
                url={`/job/${selectedId}`}
                isOpen={deleteDialogIsOpen}
                key={`job-delete-${deleteDialogIsOpen}-${selectedId}`}
                setOpen={setDeleteDialogIsOpen}
                notifyDone={handleDeleteConfirmed}
            />

            <AdminJobEditDialog
                open={isCreateDialogOpen}
                onClose={() => setIsCreateDialogOpen(false)}
                job={null}
                onSave={handleCreateJob}
            />
        </Stack>
    );
}

export default JobList;
